import React, { useRef, useEffect } from 'react';

const ELECTRODE_POSITIONS = {
    Fp1: { x: 42, y: 22 }, Fp2: { x: 78, y: 22 },
    F7:  { x: 21, y: 42 }, F3:  { x: 42, y: 42 }, Fz:  { x: 60, y: 42 }, F4:  { x: 78, y: 42 }, F8:  { x: 99, y: 42 },
    T3:  { x: 15, y: 65 }, C3:  { x: 39, y: 65 }, Cz:  { x: 60, y: 65 }, C4:  { x: 81, y: 65 }, T4:  { x: 105, y: 65 },
    T5:  { x: 24, y: 88 }, P3:  { x: 42, y: 88 }, Pz:  { x: 60, y: 88 }, P4:  { x: 78, y: 88 }, T6:  { x: 96, y: 88 },
    O1:  { x: 45, y: 105 }, O2:  { x: 75, y: 105 }
};

// Çoxnöqtəli NeuroGuide Rəng Şkalası (Piksel rəngləri üçün RGB array qaytarır)
const getZColorRGB = (z) => {
    const stops = [
        { z: -3.0, r: 0,   g: 0,   b: 255 },  // Tünd Göy
        { z: -2.0, r: 0,   g: 128, b: 255 },  // Mavi
        { z: -1.0, r: 0,   g: 255, b: 255 },  // Cyan
        { z:  0.0, r: 0,   g: 255, b: 0   },  // Yaşıl
        { z:  1.0, r: 173, g: 255, b: 0   },  // Lime
        { z:  1.5, r: 255, g: 255, b: 0   },  // Sarı
        { z:  2.0, r: 255, g: 150, b: 0   },  // Narıncı
        { z:  3.0, r: 255, g: 0,   b: 0   }   // Qırmızı
    ];

    let val = Math.max(-3, Math.min(3, z)); 

    for (let i = 0; i < stops.length - 1; i++) {
        if (val >= stops[i].z && val <= stops[i + 1].z) {
            const fraction = (val - stops[i].z) / (stops[i + 1].z - stops[i].z);
            const r = Math.round(stops[i].r + fraction * (stops[i + 1].r - stops[i].r));
            const g = Math.round(stops[i].g + fraction * (stops[i + 1].g - stops[i].g));
            const b = Math.round(stops[i].b + fraction * (stops[i + 1].b - stops[i].b));
            return [r, g, b, 255]; // RGBA
        }
    }
    return [0, 255, 0, 255]; 
};

const BrainHeatmap = ({ data, band, width = 120, height = 120 }) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        
        const internalWidth = 120;
        const internalHeight = 120;

        // Canvası təmizlə
        ctx.clearRect(0, 0, internalWidth, internalHeight);

        const imgData = ctx.createImageData(internalWidth, internalHeight);
        const pixels = imgData.data;

        // Baş modelinin parametrləri (Dairə)
        const cx = 60;
        const cy = 65;
        const radius = 50;
        const radiusSq = radius * radius;

        // Məlumatları array formasına salırıq
        const electrodes = Object.keys(ELECTRODE_POSITIONS).map(ch => ({
            x: ELECTRODE_POSITIONS[ch].x,
            y: ELECTRODE_POSITIONS[ch].y,
            val: data && data[ch] ? (data[ch][band] || 0) : 0
        }));

        // İnverse Distance Weighting (IDW) Parametri (Yayılmanın "yumşaqlığı")
        // NeuroGuide oxşarı yayılma üçün power = 3 idealdir.
        const p = 3; 

        // 1. Hər bir piksel üçün IDW (Yayılma) alqoritmi
        for (let y = 0; y < internalHeight; y++) {
            for (let x = 0; x < internalWidth; x++) {
                // Əgər piksel baş dairəsinin içindədirsə rənglə
                const distToCenterSq = Math.pow(x - cx, 2) + Math.pow(y - cy, 2);
                
                if (distToCenterSq <= radiusSq) {
                    let num = 0;
                    let den = 0;
                    let exactMatch = false;
                    let exactVal = 0;

                    for (let i = 0; i < electrodes.length; i++) {
                        const distSq = Math.pow(x - electrodes[i].x, 2) + Math.pow(y - electrodes[i].y, 2);
                        
                        if (distSq === 0) { // Tam elektrodun üstüdürsə
                            exactMatch = true;
                            exactVal = electrodes[i].val;
                            break;
                        }
                        
                        const weight = 1.0 / Math.pow(distSq, p / 2);
                        num += weight * electrodes[i].val;
                        den += weight;
                    }

                    // Təxmin edilən Z-Score dəyəri
                    const interpolatedZ = exactMatch ? exactVal : (num / den);
                    
                    // Rəngi al
                    const rgb = getZColorRGB(interpolatedZ);
                    
                    // Pikseli ImageData-ya yaz
                    const pixelIndex = (y * internalWidth + x) * 4;
                    pixels[pixelIndex] = rgb[0];     // R
                    pixels[pixelIndex + 1] = rgb[1]; // G
                    pixels[pixelIndex + 2] = rgb[2]; // B
                    pixels[pixelIndex + 3] = rgb[3]; // A (Opacity)
                }
            }
        }

        // Pikselləri canvasa yapışdır
        ctx.putImageData(imgData, 0, 0);

        // 2. Konturları və detalları çək (Dairə, qulaqlar, burun, nöqtələr)
        ctx.strokeStyle = "#333333";
        ctx.lineWidth = 1.5;

        // Xarici dairə
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, 2 * Math.PI);
        ctx.stroke();

        // Burun
        ctx.beginPath();
        ctx.moveTo(54, 14);
        ctx.lineTo(60, 6);
        ctx.lineTo(66, 14);
        ctx.stroke();

        // Qulaqlar (Sadə yarımqövslər)
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, 2 * Math.PI); // Əsas dairə var zatən, bu sadəcə vizual qapaqdır.
        ctx.stroke();

        // Elektrod nöqtələri (Ağ kiçik dairələr)
        ctx.fillStyle = "#ffffff";
        ctx.lineWidth = 0.5;
        ctx.strokeStyle = "#000000";
        for (let i = 0; i < electrodes.length; i++) {
            ctx.beginPath();
            ctx.arc(electrodes[i].x, electrodes[i].y, 1.5, 0, 2 * Math.PI);
            ctx.fill();
            ctx.stroke();
        }

    }, [data, band]);

return (
        <canvas 
            ref={canvasRef} 
            width={120}   /* Daxili ölçü həmişə 120 qalır ki, mərkəzləmə pozulmasın */
            height={120} 
            style={{ 
                width: width,   /* CSS ilə qutuya sığdırırıq */
                height: height, 
                display: 'block', 
                margin: 'auto' 
            }}
        />
    );
};

export default BrainHeatmap;